import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { botApi, BotActionType, BotTrack } from '../../api/admin.api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { BotTrackCard } from './BotTrackCard';
import {
  Bot,
  PenSquare,
  MessageSquare,
  Clock,
  Activity,
  Filter,
  CheckCircle2,
  XCircle,
  Minus,
  Plus,
  Trash2,
  MessageCircle,
} from 'lucide-react';

/** 액션 타입 한글 라벨 */
const ACTION_TYPE_LABELS: Record<BotActionType, string> = {
  SURVEY_CREATE: '설문 생성',
  COMMENT_REPLY: '댓글 응답',
  SKIP: '건너뜀',
  ERROR: '오류',
  FILTER_REJECT: '필터 거부',
  AUTO_DISABLE: '자동 비활성화',
};

/** 카테고리 한글 라벨 */
const CATEGORY_LABELS: Record<string, string> = {
  FOOD: '음식',
  DAILY: '일상',
  HOBBY: '취미',
  ENTERTAINMENT: '연예',
  TRAVEL: '여행',
  FASHION: '패션',
  PET: '반려동물',
  SPORTS: '스포츠',
  CULTURE: '문화',
  SEASON: '시즌',
};

/** 트랙 뱃지 — A = 일상(DAILY), B = 공감 찬반(EMPATHY) */
const TRACK_BADGES: Record<BotTrack, { label: string; className: string }> = {
  DAILY: { label: 'A', className: 'bg-blue-50 text-blue-700' },
  EMPATHY: { label: 'B', className: 'bg-purple-50 text-purple-700' },
};

/** 트랙 값이 없거나 모르는 값이면 "—" (배포 전 응답 등) */
const TrackBadge = ({ track }: { track?: BotTrack | null }) => {
  const badge = track ? TRACK_BADGES[track] : undefined;
  if (!badge) return <span className="text-gray-400">—</span>;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${badge.className}`}>
      {badge.label}
    </span>
  );
};

/** 필터 버튼 목록 */
const LOG_FILTERS: { label: string; value: BotActionType | null }[] = [
  { label: '전체', value: null },
  { label: '설문 생성', value: 'SURVEY_CREATE' },
  { label: '댓글 응답', value: 'COMMENT_REPLY' },
  { label: '에러', value: 'ERROR' },
  { label: '건너뜀', value: 'SKIP' },
  { label: '필터 거부', value: 'FILTER_REJECT' },
  { label: '자동 비활성화', value: 'AUTO_DISABLE' },
];

interface BotManagementProps {
  /** 트랙 B 카드의 승인 대기 → 「자동발행 대기」 탭으로 이동 */
  onGoPending?: () => void;
}

export const BotManagement = ({ onGoPending }: BotManagementProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [logFilter, setLogFilter] = useState<BotActionType | null>(null);
  const [newFeedback, setNewFeedback] = useState('');

  /* ── 데이터 조회 ── */
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['botStatus'],
    queryFn: () => botApi.getStatus().then((res) => res.data),
  });

  const { data: topicHistory = [], isLoading: historyLoading } = useQuery({
    queryKey: ['botTopicHistory'],
    queryFn: () => botApi.getTopicHistory(14).then((res) => res.data),
  });

  const { data: logs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['botLogs', logFilter],
    queryFn: () => botApi.getLogs(50, logFilter ?? undefined).then((res) => res.data),
  });

  const { data: feedbacks = [] } = useQuery({
    queryKey: ['botFeedbacks'],
    queryFn: () => botApi.getFeedbacks().then((res) => res.data),
  });

  /* ── 뮤테이션 ── */
  /* 🔴 마스터(BOT_ENABLED) 전용 — 트랙 스위치는 BotTrackCard 가 toggleTrack 으로 따로 바꾼다 (spec §4.7.1) */
  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => botApi.toggle(enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['botStatus'] });
    },
  });

  const addFeedbackMutation = useMutation({
    mutationFn: (content: string) => botApi.addFeedback(content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['botFeedbacks'] });
      setNewFeedback('');
    },
  });

  const deleteFeedbackMutation = useMutation({
    mutationFn: (id: number) => botApi.deleteFeedback(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['botFeedbacks'] });
    },
  });

  /* ── 핸들러 ── */
  const handleToggle = (checked: boolean) => {
    toggleMutation.mutate(checked);
  };

  const handleAddFeedback = () => {
    if (!newFeedback.trim()) return;
    addFeedbackMutation.mutate(newFeedback.trim());
  };

  /* ── 유틸 ── */
  const formatDateTime = (dt: string | null) => {
    if (!dt) return '—';
    const d = new Date(dt);
    return `${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const formatDate = (d: string) => {
    const date = new Date(d);
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  };

  const SuccessIcon = ({ success }: { success: boolean | null }) => {
    if (success === true) return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    if (success === false) return <XCircle className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  if (statusLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>;
  }

  return (
    <div className="space-y-6">
      {/* ── 섹션 1: 상태 카드 + 액션 ── */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Bot className="w-5 h-5" />봇 상태
        </h3>

        {/* 봇 전체 (마스터) — PUT /toggle */}
        <Card className="p-4 mb-3">
          <CardContent className="p-0 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-gray-900">봇 전체 (마스터)</div>
              <div className="text-xs text-gray-500 mt-0.5">끄면 트랙 A·B와 댓글 답글이 모두 멈춰요</div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${status?.enabled ? 'text-green-600' : 'text-red-600'}`}>
                {status?.enabled ? '켜짐' : '꺼짐'}
              </span>
              <Switch
                checked={status?.enabled ?? false}
                onCheckedChange={handleToggle}
                disabled={toggleMutation.isPending}
              />
            </div>
          </CardContent>
        </Card>

        {/* 트랙 A·B 카드 — 위아래 2행 (spec §4.8-b Q-5), 두 트랙 발행 시각을 한 화면에 (AC-21) */}
        {status?.tracks ? (
          <div className="space-y-3 mb-4">
            <BotTrackCard
              track="DAILY"
              label="트랙 A · 카테고리 순환"
              status={status.tracks.DAILY}
              masterEnabled={status.enabled}
            />
            <BotTrackCard
              track="EMPATHY"
              label="트랙 B · 일상 공감 찬반"
              status={status.tracks.EMPATHY}
              masterEnabled={status.enabled}
              onGoPending={onGoPending}
            />
          </div>
        ) : (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg text-sm text-gray-500">
            트랙 정보를 불러오지 못했어요. 새로고침해 주세요.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* 총 댓글 수 */}
          <Card className="p-4">
            <CardContent className="p-0">
              <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                <MessageSquare className="w-3.5 h-3.5" /> 총 댓글
              </div>
              <div className="text-2xl font-bold text-gray-900">{status?.totalComments ?? 0}</div>
            </CardContent>
          </Card>

          {/* 마지막 활동 */}
          <Card className="p-4">
            <CardContent className="p-0">
              <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> 마지막 활동
              </div>
              <div className="text-sm font-semibold text-gray-900">{formatDateTime(status?.lastActivity ?? null)}</div>
            </CardContent>
          </Card>
        </div>

        {/* 설문 만들기 버튼 */}
        <Button onClick={() => navigate('/admin/bot-survey-create')} className="flex items-center gap-2">
          <PenSquare className="w-4 h-4" />
          설문 만들기
        </Button>
      </div>

      {/* ── 섹션 2: 피드백 (봇 메모리) ── */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />봇 피드백 ({feedbacks.length}개)
        </h3>
        <p className="text-sm text-gray-500 mb-3">피드백은 AI 설문 생성 시 프롬프트에 반영됩니다.</p>

        {/* 피드백 목록 */}
        <div className="space-y-2 mb-3">
          {feedbacks.map((fb) => (
            <div key={fb.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-2.5">
              <span className="flex-1 text-sm text-gray-800">{fb.content}</span>
              <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(fb.regDateTime)}</span>
              <button
                onClick={() => deleteFeedbackMutation.mutate(fb.id)}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {feedbacks.length === 0 && <div className="text-sm text-gray-400 py-4 text-center">피드백이 없습니다</div>}
        </div>

        {/* 피드백 추가 */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newFeedback}
            onChange={(e) => setNewFeedback(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddFeedback()}
            placeholder="예: 선택지를 5개 이상으로 해줘"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Button size="sm" onClick={handleAddFeedback} disabled={!newFeedback.trim() || addFeedbackMutation.isPending}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* ── 섹션 3: 주제 히스토리 ── */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5" />
          주제 히스토리 (최근 14일)
        </h3>

        <Card>
          <CardContent className="p-0">
            {historyLoading ? (
              <div className="text-center py-8 text-gray-500">로딩 중...</div>
            ) : topicHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-400">아직 생성된 설문이 없어요</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left px-4 py-3 font-medium text-gray-600">날짜</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">트랙</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">카테고리</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">키워드</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">설문 ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topicHistory.map((item) => (
                      <tr key={item.id} className="border-b last:border-b-0 hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-700">{formatDate(item.postedDate)}</td>
                        <td className="px-4 py-3">
                          <TrackBadge track={item.track} />
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {CATEGORY_LABELS[item.topicCategory] ?? item.topicCategory}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-900 font-medium">{item.topicKeyword}</td>
                        <td className="px-4 py-3 text-gray-500">{item.questionId ? `#${item.questionId}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── 섹션 4: 활동 로그 ── */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Filter className="w-5 h-5" />
          활동 로그
        </h3>

        {/* 필터 버튼 */}
        <div className="flex flex-wrap gap-2 mb-4">
          {LOG_FILTERS.map((f) => (
            <Button
              key={f.label}
              variant={logFilter === f.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setLogFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <Card>
          <CardContent className="p-0">
            {logsLoading ? (
              <div className="text-center py-8 text-gray-500">로딩 중...</div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8 text-gray-400">해당 타입의 로그가 없어요</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left px-4 py-3 font-medium text-gray-600">시간</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">트랙</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">타입</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">상세</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-600">성공</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">설문</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">댓글</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} className="border-b last:border-b-0 hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDateTime(log.regDateTime)}</td>
                        <td className="px-4 py-3">
                          <TrackBadge track={log.track} />
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              log.actionType === 'ERROR' || log.actionType === 'AUTO_DISABLE'
                                ? 'bg-red-50 text-red-700'
                                : log.actionType === 'SURVEY_CREATE'
                                  ? 'bg-green-50 text-green-700'
                                  : log.actionType === 'COMMENT_REPLY'
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {ACTION_TYPE_LABELS[log.actionType] ?? log.actionType}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={log.detail ?? ''}>
                          {log.detail ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <SuccessIcon success={log.success} />
                        </td>
                        <td className="px-4 py-3 text-gray-500">{log.questionId ? `#${log.questionId}` : '—'}</td>
                        <td className="px-4 py-3 text-gray-500">{log.commentId ? `#${log.commentId}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
